import React from 'react';
import { useSelector } from 'react-redux';

import DescriptionList from '@anwb/poncho/components/description-list';
import { List, ListItem } from '@anwb/poncho/components/list';
import Panel from '@anwb/poncho/components/panel';
import Typography from '@anwb/poncho/components/typography';
import { formatLicensePlate } from '@anwb/poncho/utilities/license-plate';
import { PanelEditHeader } from 'components/RequestFlow/components/PanelEditHeader/PanelEditHeader';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import {
	carFuelTypeSelector,
	chosenColorSelector,
	chosenOptionsSelector,
	chosenServicesSelector,
	chosenUpgradeSelector,
	isNewCarSelector,
	isOccasionSelector,
	leaseCarDataIdSelector,
	leaseCarDataNameSelector,
	leaseCarOptionsSelector,
	leaseCarServicesSelector,
} from 'store/selectors';
import { GearType, GearTypeKey } from 'types';
import { STEPS, getStepPathWithCarParam } from 'utils/navigation';

import { getAvailableOptionsForOccasion } from '../../CarContract/components/utils';
import { customOverviewChangeGA4TrackEvent } from '../util';

export const ColorAndOptions = () => {
	const {
		options,
		services,
		selectedColor,
		selectedOptions,
		selectedServices,
		selectedUpgrade,
		isOccasion,
		isNewCar,
		licensePlate,
		carName,
		fuelType,
	} = {
		options: useSelector(leaseCarOptionsSelector),
		services: useSelector(leaseCarServicesSelector),
		selectedColor: useSelector(chosenColorSelector),
		selectedOptions: useSelector(chosenOptionsSelector),
		selectedServices: useSelector(chosenServicesSelector),
		selectedUpgrade: useSelector(chosenUpgradeSelector),
		isOccasion: useSelector(isOccasionSelector),
		isNewCar: useSelector(isNewCarSelector),
		licensePlate: useSelector(leaseCarDataIdSelector),
		carName: useSelector(leaseCarDataNameSelector),
		fuelType: useSelector(carFuelTypeSelector),
	};
	const gaFunnelInfo = useGaFunnelInfo();

	const carSpecs = [
		{ key: 'Uitvoering', value: selectedUpgrade?.description },
		...(isNewCar ? [] : [{ key: 'Brandstof', value: fuelType }]),
		{
			key: 'Transmissie',
			value: GearType[selectedUpgrade?.vehicleData.gearType as GearTypeKey] ?? '-',
		},
		{ key: 'Kleur', value: selectedColor?.name },
		...(licensePlate && !isNewCar
			? [{ key: 'Kenteken', value: formatLicensePlate(licensePlate) }]
			: []),
	];

	const renderOptionsAndServices = () => (
		<List variant="bulleted">
			{selectedOptions.map((option) => (
				<ListItem key={option.id}>{option.description}</ListItem>
			))}
			{selectedServices.map((service) => (
				<ListItem key={service.id}>{service.description}</ListItem>
			))}
		</List>
	);

	const selectedOptionsAndServices =
		selectedOptions.length || selectedServices.length
			? [
					{
						key: 'Opties',
						value: renderOptionsAndServices(),
					},
				]
			: [];

	const title = isNewCar ? 'Voertuig en opties' : 'Gekozen opties';

	const handleClick = (componentTitle: string) => {
		customOverviewChangeGA4TrackEvent(componentTitle, gaFunnelInfo);
	};

	const getChosenOptionsAndServicesForOccasion = () => {
		const hasOptionsOrServices = getAvailableOptionsForOccasion(options).length + services.length;

		return hasOptionsOrServices ? (
			<Panel
				hasOuterSpacing={false}
				id="selected-color-options-overview-occasion"
				dataTest="selected-color-options-overview-occasion"
			>
				<Panel.Content>
					<PanelEditHeader
						title={title}
						onClick={() => handleClick(title)}
						getStepPath={getStepPathWithCarParam}
						stepToEdit={STEPS.CAR_CONTRACT}
						dataTest="selected-color-options-overview-occasion-change-link"
					/>
					{selectedOptionsAndServices.length ? (
						<DescriptionList items={selectedOptionsAndServices} dataTest="options-occasion" />
					) : (
						<Typography variant="support-text" dataTest="no-selection-disclaimer" as="span">
							Je hebt geen opties gekozen.
						</Typography>
					)}
				</Panel.Content>
			</Panel>
		) : null;
	};

	return isNewCar ? (
		<Panel
			id="selected-color-options-overview"
			hasOuterSpacing={false}
			dataTest="selected-color-options-overview"
		>
			<Panel.Content>
				<PanelEditHeader
					title={title}
					onClick={() => handleClick(title)}
					getStepPath={getStepPathWithCarParam}
					stepToEdit={STEPS.CAR_CONTRACT}
					dataTest="selected-color-options-overview-change-link"
				/>
				<DescriptionList
					items={[...carSpecs, ...selectedOptionsAndServices]}
					dataTest="colorAndOptions"
				/>
			</Panel.Content>
		</Panel>
	) : (
		<>
			<Panel dataTest="occasion-car-specs" hasOuterSpacing={false}>
				<Panel.Header title={carName} />
				<Panel.Content>
					<DescriptionList items={carSpecs} dataTest="specs-occasion" />
				</Panel.Content>
			</Panel>
			{isOccasion && getChosenOptionsAndServicesForOccasion()}
		</>
	);
};
